import { useState } from 'react';
import { Link } from 'react-router';
import * as bi from 'react-bootstrap-icons';
import { IClientCityBrief } from '@bustickus/models-client';
import { tripsPageUrl } from 'common/navigation';
import { Box, Checkbox, Flex, Header, Input } from 'components';
import { ModalCalendarPrice } from 'shared/components';
import { ModalSelectCity } from '..';
import { getCityArrival, getCityDeparture, getDateNextWeek, getDateString, getDateTomorrow } from './helpers';
import './style.scss';

type SelectCityModalType = 'arrival' | 'departure';

interface IProps {
  arrivalCityId: string | null;
  departureCityId: string | null;
  departureDate: Date | null;
  returnDate: Date | null;

  cities: IClientCityBrief[];
  onSearchClick: (
    departureCityId: string,
    arrivalCityId: string,
    departureDate: Date,
    returnDate: Date | undefined | null
  ) => void;
}

function FilterBlock(props: IProps) {
  const [selectCityModal, setSelectCityModal] = useState<SelectCityModalType | null>(null);
  const [departureDateModal, setDepartureDateModal] = useState<boolean>(false);
  const [returnDateModal, setReturnDateModal] = useState<boolean>(false);

  const [departureDate, setDepartureDate] = useState<Date>(props.departureDate ?? getDateTomorrow());
  const [returnDate, setReturnDate] = useState<Date>(props.returnDate ?? getDateNextWeek());
  const [noNeedReturnTicket, setNoNeedReturnTicket] = useState<boolean>(props.returnDate == null);

  const [departureCity, setDepartureCity] = useState<IClientCityBrief | null>(
    getCityDeparture(props.departureCityId, props.cities)
  );
  const [arrivalCity, setArrivalCity] = useState<IClientCityBrief | null>(
    getCityArrival(props.arrivalCityId, props.cities)
  );

  const isFilterValid = arrivalCity != null && departureCity != null;

  const handleCityModalSelect = (city: IClientCityBrief) => {
    if (selectCityModal === 'arrival') {
      setArrivalCity(city);

      if (departureCity != null) {
        search(departureCity.id, city.id, departureDate, noNeedReturnTicket ? null : returnDate);
      }
    } else if (selectCityModal === 'departure') {
      setDepartureCity(city);

      if (arrivalCity != null) {
        search(city.id, arrivalCity.id, departureDate, noNeedReturnTicket ? null : returnDate);
      }
    }

    setSelectCityModal(null);
  };

  const handleDepartureDateChange = (date: Date) => {
    setDepartureDate(date);
    setDepartureDateModal(false);

    if (departureCity != null && arrivalCity != null) {
      search(departureCity.id, arrivalCity.id, date, noNeedReturnTicket ? null : returnDate);
    }
  };

  const handleReturnDateChange = (date: Date) => {
    setReturnDate(date);
    setReturnDateModal(false);

    if (departureCity != null && arrivalCity != null) {
      search(departureCity.id, arrivalCity.id, departureDate, noNeedReturnTicket ? null : date);
    }
  };

  const handleSearchClick = () => {
    if (arrivalCity == null || departureCity == null) {
      return;
    }

    search(departureCity.id, arrivalCity.id, departureDate, noNeedReturnTicket ? null : returnDate);
  };

  const handleNoNeedReturnTicketChange = (noNeedReturnTicket: boolean) => {
    setNoNeedReturnTicket(noNeedReturnTicket);

    if (departureCity != null && arrivalCity != null) {
      search(departureCity.id, arrivalCity.id, departureDate, noNeedReturnTicket ? null : returnDate);
    }
  };

  const search = (departureCityId: string, arrivalCityId: string, departureDate: Date, returnDate: Date | null) => {
    window.history.pushState({}, '', tripsPageUrl(departureCityId, arrivalCityId, departureDate, returnDate));
    props.onSearchClick(departureCityId, arrivalCityId, departureDate, returnDate);
  };

  return (
    <Box padding={20}>
      {selectCityModal != null && (
        <ModalSelectCity
          cities={props.cities}
          onClose={() => setSelectCityModal(null)}
          onSelect={handleCityModalSelect}
        />
      )}

      {departureDateModal && departureCity != null && arrivalCity != null && (
        <ModalCalendarPrice
          departureCity={{
            id: departureCity.id,
            name: departureCity.name,
          }}
          arrivalCity={{
            id: arrivalCity.id,
            name: arrivalCity.name,
          }}
          date={departureDate}
          onClose={() => setDepartureDateModal(false)}
          onAccept={handleDepartureDateChange}
        />
      )}

      {returnDateModal && departureCity != null && arrivalCity != null && (
        <ModalCalendarPrice
          arrivalCity={{
            id: departureCity.id,
            name: departureCity.name,
          }}
          departureCity={{
            id: arrivalCity.id,
            name: arrivalCity.name,
          }}
          date={departureDate}
          onClose={() => setReturnDateModal(false)}
          onAccept={handleReturnDateChange}
        />
      )}

      <Flex
        direction='column'
        gap={10}
      >
        <Header>Поиск билетов</Header>
        <div className='filter-block-main-knAhsy'>
          <div className='filter-block-child-1u5AS'>
            <Input
              readonly
              type='text'
              icon={<bi.PinMapFill />}
              label='Город отправления'
              placeholder='Нажмите для выбора'
              onClick={() => setSelectCityModal('departure')}
              value={departureCity?.name ?? ''}
            />
            <Input
              readonly
              type='text'
              icon={<bi.PinMapFill />}
              label='Город назначения'
              placeholder='Нажмите для выбора'
              onClick={() => setSelectCityModal('arrival')}
              value={arrivalCity?.name ?? ''}
            />
          </div>
          <div className='filter-block-child-1u5AS'>
            <Input
              type='text'
              label='Дата отправления'
              disabled={departureCity == null || arrivalCity == null}
              readonly
              icon={<bi.CalendarEvent />}
              value={getDateString(departureDate)}
              onClick={() => setDepartureDateModal(true)}
            />

            <Flex
              direction='column'
              gap={5}
              className='back-ticket-block-Naka1'
            >
              <Input
                type='text'
                label='Дата обратного билета'
                icon={<bi.CalendarEvent />}
                disabled={noNeedReturnTicket || departureCity == null || arrivalCity == null}
                value={getDateString(returnDate)}
                onClick={() => setReturnDateModal(true)}
              />
              <Checkbox
                checked={noNeedReturnTicket}
                onChange={handleNoNeedReturnTicketChange}
              >
                Обратный билет не нужен
              </Checkbox>
            </Flex>
          </div>
          <Link
            onClick={handleSearchClick}
            to={tripsPageUrl(departureCity?.id, arrivalCity?.id, departureDate, noNeedReturnTicket ? null : returnDate)}
            aria-disabled={!isFilterValid ? true : undefined}
            className='search-btn-vg8u'
          >
            <div />
            <Flex
              gap={10}
              alignItems='center'
            >
              <bi.Search /> <span>Поиск</span>
            </Flex>
            <div />
          </Link>
        </div>
      </Flex>
    </Box>
  );
}

export default FilterBlock;
